import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '@/components/BrandLogo';
import { useAuth } from '@/context/AuthContext';
import { API, BACKEND_URL } from '@/lib/api';
import { AUTHENTICATED_HOME_PATH } from '@/lib/sidebarNav';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Lock } from 'lucide-react';
import { toast } from 'sonner';
import './AuthPage.css';

export default function AuthPage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) {
    navigate(user.onboarding_done ? AUTHENTICATED_HOME_PATH : '/onboarding', { replace: true });
    return null;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/password-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Incorrect password');
      }
      const data = await response.json();
      login(data.user, data.session_token);
      toast.success('Welcome back!');
      navigate(data.user.onboarding_done ? AUTHENTICATED_HOME_PATH : '/onboarding');
    } catch (error) {
      const message = error?.message === 'Failed to fetch'
        ? `Unable to reach the backend at ${BACKEND_URL || 'the configured API URL'}.`
        : (error?.message || 'Authentication failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page__shell">
        <div className="auth-page__topbar">
          <a href="/" className="auth-page__back">
            <ArrowLeft size={15} />
            <span>Back</span>
          </a>
          <div className="auth-page__brand">
            <BrandLogo variant="mark" className="auth-page__brand-mark" />
            <span>Advoq</span>
          </div>
        </div>

        <div className="auth-center">
          <div className="auth-card">
            <h1 className="auth-card__title">Welcome back.</h1>

            <form onSubmit={handleSubmit} className="auth-form">
              <label className="auth-field">
                <span className="auth-field__label">Password</span>
                <div className="auth-field__control">
                  <div className="auth-field__icon"><Lock size={15} /></div>
                  <input
                    data-testid="auth-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="auth-field__input auth-field__input--password"
                    placeholder="Enter password"
                    autoFocus
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="auth-field__toggle"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </label>

              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading}
                className="auth-submit"
              >
                {loading ? <div className="auth-submit__spinner" /> : null}
                <span>Sign In</span>
                {!loading ? <ArrowRight size={15} /> : null}
              </button>
            </form>

            <p className="auth-card__legal">
              By continuing you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
